import React from 'react'
import { Router, RouteComponentProps, NavigateFn } from '@reach/router'
import Appbar from '../Appbar'
import { Tag, ProgressBar, Classes, Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import {
  PageContent,
  Flex,
  Button,
  IconButton,
  ModalLayer,
  PrettyNumber,
  PrettyDate,
  FileDownloadLink,
  NewGridTable,
  IGridTableColumn,
  GridTableStatusEnum,
} from '@ui'
import { Permissions, PermissionsType, IPermissionsOnEmployeeList } from '@permissions'
import useImportHistory from './hooks/useImportHistory'
import useImportProgress from './hooks/usePollStatus'
import { IEmployeeImport } from '@api'
import track from '@track'
import Event from '@track/Event'
import { DateUtil } from '@utils'
import css from './employeeManagement.module.scss'

const statusIntents: { [key: string]: 'success' | 'danger' } = {
  completed: 'success',
  failed: 'danger',
  invalid_delimiter: 'danger',
}

function placeholder(string = 'placeholder') {
  return <div className={Classes.SKELETON}>{string}</div>
}

export default function ImportHistoryPage({ navigate }: RouteComponentProps) {
  const { imports, pageInfo, loading, loadingMore, loadMore, reload } = useImportHistory()

  const columns: IGridTableColumn<IEmployeeImport>[] = [
    {
      label: 'Import',
      cellStyle: {
        minWidth: 400,
      },
      Cell: ({ cell }) => {
        if (!cell) return placeholder()

        return cell.data.status === 'completed' ? (
          <CompletedImportLink i={cell.data} navigate={navigate} />
        ) : cell.data.status === 'failed' ? (
          <FailedImportLink i={cell.data} />
        ) : (
          <UpdatingImportLink i={cell.data} navigate={navigate} />
        )
      },
    },
    {
      label: 'Total',
      alignRight: true,

      Cell: ({ cell }) => {
        if (!cell) return placeholder('sh')

        return <PrettyNumber value={cell.data.totalCount} />
      },
    },
    {
      label: 'Created',
      alignRight: true,

      Cell: ({ cell }) => {
        if (!cell) return placeholder('sh')

        return <PrettyNumber value={cell.data.createdCount} />
      },
    },
    {
      label: 'Updated',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return <PrettyNumber value={cell.data.updatedCount} />
      },
    },
    {
      label: 'Removed',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return placeholder('sh')

        return <PrettyNumber value={cell.data.destroyedCount} />
      },
    },
    {
      label: 'Failed',
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return placeholder('sh')

        return <PrettyNumber value={cell.data.failedCount} />
      },
    },
    {
      label: 'Files',
      cellStyle: { minWidth: 200 },
      Cell: ({ cell }) => {
        if (!cell) return placeholder()

        return (
          <span
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              track.employeeImportHistory({
                eventName: Event.IMPORT,
                props: {
                  type: 'file',
                },
              })
            }}>
            {cell.data.url && (
              <Tooltip2 content="Download imported file">
                <FileDownloadLink url={cell.data.url}>Imported</FileDownloadLink>
              </Tooltip2>
            )}
            {cell.data.url && cell.data.backupUrl && ' | '}
            {cell.data.backupUrl && (
              <span
                onClick={() => {
                  track.employeeImportHistory({
                    eventName: Event.IMPORT,
                    props: {
                      type: 'backup',
                    },
                  })
                }}>
                <Tooltip2 content="Download backup file">
                  <FileDownloadLink url={cell.data.backupUrl}>Backup</FileDownloadLink>
                </Tooltip2>
              </span>
            )}
          </span>
        )
      },
    },
    {
      label: 'Status',
      Cell: ({ cell }) => {
        if (!cell) return placeholder()

        return cell.data.status === 'completed' || cell.data.status === 'failed' ? (
          <Tag
            round
            minimal
            intent={statusIntents[cell.data.status]}
            style={{ textTransform: 'capitalize' }}>
            {cell.data.status}
          </Tag>
        ) : (
          <StatusProgress id={cell.data.id} />
        )
      },
    },
  ]

  const format = (items: IEmployeeImport[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formated = format(imports)

  return (
    <>
      <Appbar
        title={
          <Flex>
            <span style={{ marginRight: 8 }}>
              <Tooltip2 content="Back to Employee Management" placement={Position.BOTTOM}>
                <IconButton
                  minimal
                  iconName="chevron-left"
                  onClick={() => {
                    navigate?.('/employees')

                    track.employeeImportHistory({
                      eventName: Event.NAVIGATE,
                      props: {
                        pageName: '/employees',
                      },
                    })
                  }}
                />
              </Tooltip2>
            </span>
            Import History
          </Flex>
        }
      />
      {/* eslint-disable @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Router primary={false} navigate={navigate}>
        <ModalLayer path="*" reload={reload} />
      </Router>
      <PageContent>
        <Toolbar navigate={navigate} />
        <NewGridTable<IEmployeeImport>
          sticky={true}
          status={
            loading
              ? GridTableStatusEnum.LOADING
              : loadingMore
              ? GridTableStatusEnum.PAGING
              : GridTableStatusEnum.IDLE
          }
          minCellWidth="auto"
          page={{
            hasNextPage: pageInfo?.hasNextPage || false,
            endCursor: pageInfo?.endCursor || '',
            onNextPage: () => {
              loadMore()

              track.employeeImportHistory({
                eventName: Event.PAGE,
              })
            },
          }}
          columns={columns}
          data={formated}
          track={{
            select: () => {
              track.employeeImportHistory({
                eventName: Event.ROW_SELECT,
              })
            },
            selectAll: () => {
              track.employeeImportHistory({
                eventName: Event.SELECT_ALL,
              })
            },
          }}
          headerTop={52}
        />
      </PageContent>
    </>
  )
}

function StatusProgress({ id }: { id: string }) {
  const { progress, newStatus } = useImportProgress(id)
  if (newStatus === 'invalid_delimiter') {
    return (
      <Tooltip2
        content={
          'Invalid delimiter. Please use a valid delimiter. Allowed delimiters are [, \\t ;]'
        }>
        <Tag
          round
          minimal
          intent={statusIntents[newStatus]}
          style={{ textTransform: 'capitalize' }}>
          failed
        </Tag>
      </Tooltip2>
    )
  } else if (newStatus === 'completed' || newStatus === 'failed') {
    return (
      <Tag round minimal intent={statusIntents[newStatus]} style={{ textTransform: 'capitalize' }}>
        {newStatus}
      </Tag>
    )
  } else if (newStatus === 'canceled') {
    return (
      <Tooltip2 content="File has been Canceled">
        <Tag round minimal>
          Canceled
        </Tag>
      </Tooltip2>
    )
  } else if (newStatus === 'pending_approval') {
    return (
      <Tooltip2 content="File is waiting of approval">
        <Tag round minimal>
          Pending Approval
        </Tag>
      </Tooltip2>
    )
  } else if (progress === null) {
    return (
      <Tooltip2 content="File is waiting to be processed">
        <Tag round minimal>
          Pending
        </Tag>
      </Tooltip2>
    )
  } else {
    return <ProgressBar value={progress} stripes={false} />
  }
}

function FailedImportLink({ i }: { i: IEmployeeImport }) {
  return (
    <div>
      <PrettyDate value={i.createdAt} timeStringFormat={'YYYY-MM-DD hh:mm:ss'} />{' '}
      {DateUtil(i.createdAt).format('h:mm A')}
      <span style={{ color: 'var(--grey1)' }}> by {i.importedBy}</span>
    </div>
  )
}

function CompletedImportLink({ i, navigate }: { i: IEmployeeImport; navigate?: NavigateFn }) {
  return (
    <div
      style={{ cursor: 'pointer' }}
      onClick={() => {
        navigate?.(`/employees/history/${i.id}`)

        track.employeeImportHistory({
          eventName: Event.NAVIGATE,
          props: {
            pageName: `/employees/history/${i.id}`,
          },
        })
      }}>
      <Tooltip2 content="Go to Import Details">
        <div className={css.hover_underline}>
          <PrettyDate value={i.createdAt} timeStringFormat="YYYY-MM-DD hh:mm:ss" /> @{' '}
          {DateUtil(i.createdAt).format('h:mm A')}
        </div>
      </Tooltip2>
      <span style={{ color: 'var(--grey1)' }}> by {i.importedBy}</span>
    </div>
  )
}

function UpdatingImportLink({ i, navigate }: { navigate?: NavigateFn; i: IEmployeeImport }) {
  const { newStatus } = useImportProgress(i.id)

  return newStatus === 'completed' ? (
    <CompletedImportLink i={i} navigate={navigate} />
  ) : newStatus === 'failed' ? (
    <FailedImportLink i={i} />
  ) : (
    <div
      style={{ cursor: 'pointer' }}
      onClick={() => {
        navigate?.('?newItem=employeeImport')

        track.employeeImportDialog({
          eventName: Event.NEW_RECORD_DIALOG,
        })
      }}>
      <PrettyDate value={i.createdAt} timeStringFormat="YYYY-MM-DD hh:mm:ss" /> @
      {DateUtil(i.createdAt).format('h:mm A')}
      <span style={{ color: 'var(--grey1)' }}> by {i.importedBy}</span>
    </div>
  )
}

function Toolbar({ navigate }: { navigate?: NavigateFn }) {
  return (
    <Permissions type={PermissionsType.EMPLOYEE_LIST} on={[]}>
      {(permissions) => (
        <Flex className={css.toolbar}>
          <div />
          {(permissions as IPermissionsOnEmployeeList).canImport && (
            <Button
              text="New Import"
              iconName="plus"
              onClick={() => {
                navigate?.('?newItem=employeeImport')

                track.employeeImportDialog({
                  eventName: Event.NEW_RECORD_DIALOG,
                })
              }}
            />
          )}
        </Flex>
      )}
    </Permissions>
  )
}
